import config from 'config/environment';
import { useMemo } from 'react';
import { ConsoleLogsManager } from './console';
import { SentryLogsManager } from './sentry';
import { ILogsManager, LogLevelEnum } from './logsManager.types';
import { useFeatureFlags } from '../../contexts/featureFlag/useFeatureFlags';

const logsLevelMapping: {
  [key: string]: number;
} = {
  error: 0,
  warning: 1,
  info: 2,
  debug: 3,
};

const defualtLogsLevel = logsLevelMapping[config.LOGS_LEVEL];
const defaultLogsProviders = config.LOGS_PROVIDERS;

const providerImplementations: any = {
  console: ConsoleLogsManager,
  sentry: SentryLogsManager,
};

const resolveProviders = (providers: string[]): ILogsManager[] => {
  return providers
    .map((provider) => {
      const Provider = providerImplementations[provider];
      if (Provider) {
        return new Provider();
      }
      return null;
    })
    .filter((provider) => !provider);
};

export function useLogsManager(): ILogsManager {
  const { featureFlags } = useFeatureFlags();
  const logsProvidersFlag = featureFlags['logs-providers'];
  const logsLevelFlag = featureFlags['logs-level'];

  const logManager = useMemo(() => {
    const providers = resolveProviders(
      (logsProvidersFlag ?? defaultLogsProviders) as string[]
    );
    const level = logsLevelFlag
      ? logsLevelMapping[logsLevelFlag] || defualtLogsLevel
      : defualtLogsLevel;

    const logError = (error: Error | string, data?: any) => {
      try {
        providers.forEach((provider) => provider.logError(error, data));
      } catch (e) {
        console.error('LogManager error', e);
      }
    };

    const logWarning = (message: string, data?: any) => {
      if (level < LogLevelEnum.warning) return;
      try {
        providers.forEach((provider) => provider.logWarning(message, data));
      } catch (e) {
        console.error('LogManager error', e);
      }
    };

    const logInfo = (message: string, data?: any) => {
      if (level < LogLevelEnum.info) return;
      try {
        providers.forEach((provider) => provider.logInfo(message, data));
      } catch (e) {
        console.error('LogManager error', e);
      }
    };

    const logDebug = (message: string, data?: any) => {
      if (level < LogLevelEnum.debug) return;
      try {
        providers.forEach((provider) => provider.logDebug(message, data));
      } catch (e) {
        console.error('LogManager error', e);
      }
    };

    return {
      logError,
      logWarning,
      logInfo,
      logDebug,
    };
  }, [logsLevelFlag, logsProvidersFlag]);

  return logManager;
}
